// =============================================================================
// features/crm/components/TriageLeadDialog.tsx — Destinar um lead da fila de
// triagem (F55-S07): define a cidade e/ou o responsável.
//
// Regras (servidor é a fonte — a tela só antecipa a mensagem):
//   - Pelo menos um dos dois campos.
//   - Assumir para si é livre; entregar a outra pessoa exige `leads:transfer`
//     (403 LEAD_TRANSFER_FORBIDDEN vira mensagem no próprio diálogo).
//   - Outro triador chegou antes: 409 LEAD_NOT_IN_TRIAGE → avisa e fecha (a
//     fila já foi recarregada pela mutation).
//
// DS: mesmo diálogo de NewLeadModal (portal, backdrop, elev-5, fade-up,
// Escape fecha) + Select/Button primitivos.
// LGPD: mostra só o nome do lead (o triador já o vê na fila) — sem telefone.
// =============================================================================

import * as React from 'react';
import { createPortal } from 'react-dom';

import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { useToast } from '../../../components/ui/Toast';
import { useAssignableAgents } from '../../../hooks/admin/useAgents';
import { useCitiesList } from '../../../hooks/useCitiesList';
import { cn } from '../../../lib/cn';
import { triageErrorOutcome, useTriageLead } from '../triage';

export interface TriageLeadDialogProps {
  lead: { id: string; name: string } | null;
  onClose: () => void;
}

export function TriageLeadDialog({
  lead,
  onClose,
}: TriageLeadDialogProps): React.JSX.Element | null {
  const { toast } = useToast();
  const { cities, isLoading: citiesLoading } = useCitiesList();
  const { agents, isLoading: agentsLoading } = useAssignableAgents();
  const { mutateAsync, isPending } = useTriageLead();

  const [cityId, setCityId] = React.useState('');
  const [agentId, setAgentId] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const open = lead !== null;

  // Reabrir limpa o formulário — cada lead começa do zero.
  React.useEffect(() => {
    if (open) {
      setCityId('');
      setAgentId('');
      setError(null);
    }
  }, [open, lead?.id]);

  React.useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!lead) return null;

  const nothingChosen = cityId === '' && agentId === '';

  const submit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (nothingChosen) {
      setError('Escolha a cidade, o responsável ou os dois.');
      return;
    }
    setError(null);
    try {
      await mutateAsync({
        leadId: lead.id,
        ...(cityId !== '' ? { cityId } : {}),
        ...(agentId !== '' ? { agentId } : {}),
      });
      toast('Lead destinado — saiu da fila de triagem.', 'success');
      onClose();
    } catch (err) {
      const outcome = triageErrorOutcome(err);
      if (outcome.kind === 'stale') {
        toast('Este lead já foi destinado por outra pessoa. A fila foi atualizada.', 'info');
        onClose();
        return;
      }
      setError(outcome.message);
    }
  };

  // Opção vazia SELECIONÁVEL (o `placeholder` do Select é disabled): voltar a
  // "não mudar" depois de escolher precisa ser possível.
  const cityOptions = [
    { value: '', label: citiesLoading ? 'Carregando cidades…' : 'Manter sem cidade' },
    ...cities.map((c) => ({ value: c.id, label: `${c.name} — ${c.state_uf}` })),
  ];
  const agentOptions = [
    { value: '', label: agentsLoading ? 'Carregando responsáveis…' : 'Manter sem responsável' },
    ...agents.map((a) => ({ value: a.id, label: a.display_name })),
  ];

  return createPortal(
    <>
      <div
        role="presentation"
        aria-hidden="true"
        className="fixed inset-0 z-[150] bg-[var(--text)]/20 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-[160] flex items-center justify-center p-4 pointer-events-none">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="triage-dialog-title"
          aria-describedby="triage-dialog-desc"
          className={cn(
            'w-full max-w-md pointer-events-auto',
            'rounded-lg border border-border bg-surface-1',
            'animate-[fade-up_300ms_cubic-bezier(0.16,1,0.3,1)_both]',
          )}
          style={{ boxShadow: 'var(--elev-5)' }}
        >
          <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-border-subtle">
            <div className="min-w-0">
              <h2
                id="triage-dialog-title"
                className="font-display font-bold text-ink"
                style={{
                  fontSize: 'var(--text-xl)',
                  letterSpacing: '-0.03em',
                  fontVariationSettings: "'opsz' 24",
                }}
              >
                Destinar lead
              </h2>
              <p id="triage-dialog-desc" className="font-sans text-sm text-ink-3 mt-1 truncate">
                {lead.name}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar"
              className={cn(
                'w-8 h-8 shrink-0 flex items-center justify-center rounded-sm text-ink-3',
                'hover:text-ink hover:bg-surface-hover transition-all duration-fast ease',
                'focus-visible:ring-2 focus-visible:ring-azul/20',
              )}
            >
              <svg
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.6}
                className="w-5 h-5"
                aria-hidden="true"
              >
                <path d="M5 5l10 10M15 5l-10 10" />
              </svg>
            </button>
          </div>

          <form
            onSubmit={(e) => {
              void submit(e);
            }}
            noValidate
            className="px-6 py-5 flex flex-col gap-4"
          >
            <Select
              id="triage-city"
              label="Cidade"
              options={cityOptions}
              value={cityId}
              onChange={(e) => setCityId(e.target.value)}
              hint="Com cidade, o lead entra na fila dessa cidade."
            />
            <Select
              id="triage-agent"
              label="Responsável"
              options={agentOptions}
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              hint="Atribuir a outra pessoa exige a permissão de transferir leads."
            />

            {error && (
              <p role="alert" className="font-sans text-sm text-danger">
                {error}
              </p>
            )}

            <div className="flex justify-end gap-3 pt-1">
              <Button type="button" variant="ghost" onClick={onClose} disabled={isPending}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" disabled={isPending || nothingChosen}>
                {isPending ? 'Destinando…' : 'Destinar'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body,
  );
}
